// src/lib/travelHealth/coAdmin/coAdminRules.ts
//
// Structured data only — no logic. Co-administration rules for the travel
// health matrix engine. ruleText and schedulingNote are verbatim clinical
// instructions and must not be paraphrased.

import { PGD_IDS } from "../pgds/pgdIds";

export type CoAdminRuleType =
  | "timing" // specific interval required
  | "advisory" // best-practice spacing, no hard interval
  | "product-conditional"; // constraint depends on which brand is
// selected within the PGD — flag in advance

export interface CoAdminRuleDefinition {
  ruleId: string;
  // Use PgdId values. Use null to indicate "any other vaccine
  // in the basket" — the engine handles the expansion.
  pgdIdA: string;
  pgdIdB: string | null;
  type: CoAdminRuleType;
  // Verbatim rule text for display to the pharmacist.
  // Do not paraphrase — copy exactly as specified.
  ruleText: string;
  // Optional scheduling note — concrete guidance on sequencing.
  // May reference departure date using the placeholder
  // {departureDate} which the engine will replace at runtime.
  schedulingNote?: string;
}

export const CO_ADMIN_RULES: CoAdminRuleDefinition[] = [
  // --- RULE 1: Two injected live vaccines ---
  {
    ruleId: "live-injected-yf-dengue",
    pgdIdA: PGD_IDS.YELLOW_FEVER,
    pgdIdB: PGD_IDS.DENGUE,
    type: "timing",
    ruleText:
      "Yellow fever and dengue are both injected live vaccines. Administer on the same day at separate sites, or at least four weeks apart. Do not administer on different days within the same four-week window.",
    schedulingNote:
      "Recommended: give both today at separate sites. If given on separate days, the second vaccine cannot be administered until at least four weeks after the first.",
  },

  // --- RULE 2: Two oral live vaccines ---
  {
    ruleId: "live-oral-typhoid-cholera",
    pgdIdA: PGD_IDS.TYPHOID,
    pgdIdB: PGD_IDS.CHOLERA,
    type: "timing",
    ruleText:
      "If oral typhoid (Vivotif) and oral cholera (Vaxchora) are both selected: allow at least two hours between doses. This restriction does not apply if injectable typhoid (TYPHIM Vi) is used instead of Vivotif, or if Dukoral is used instead of Vaxchora.",
    schedulingNote:
      "Give oral typhoid and oral cholera at least two hours apart within the same visit.",
  },

  // --- RULE 3: Oral typhoid + doxycycline ---
  {
    ruleId: "typhoid-oral-doxycycline",
    pgdIdA: PGD_IDS.TYPHOID,
    pgdIdB: PGD_IDS.DOXYCYCLINE,
    type: "product-conditional",
    ruleText:
      "If oral typhoid (Vivotif) is selected: complete the full three-dose Vivotif course at least three days before starting doxycycline. Doxycycline interferes with the live attenuated vaccine strain. This restriction does not apply to injectable typhoid (TYPHIM Vi).",
    schedulingNote:
      "If using Vivotif: the three-dose course must be completed at least three days before doxycycline begins. Doxycycline for malaria prophylaxis starts one to two days before departure ({departureDate}). Plan accordingly.",
  },

  // --- RULE 4: Oral typhoid + mefloquine ---
  {
    ruleId: "typhoid-oral-mefloquine",
    pgdIdA: PGD_IDS.TYPHOID,
    pgdIdB: PGD_IDS.MEFLOQUINE,
    type: "product-conditional",
    ruleText:
      "If oral typhoid (Vivotif) is selected: complete the full three-dose Vivotif course at least three days before the first mefloquine dose. This restriction does not apply to injectable typhoid (TYPHIM Vi).",
    schedulingNote:
      "If using Vivotif: the three-dose course must be completed at least three days before mefloquine begins. Mefloquine starts ten days before departure ({departureDate}). Plan accordingly.",
  },

  // --- RULE 5: Oral cholera (Vaxchora) + doxycycline ---
  {
    ruleId: "cholera-vaxchora-doxycycline",
    pgdIdA: PGD_IDS.CHOLERA,
    pgdIdB: PGD_IDS.DOXYCYCLINE,
    type: "product-conditional",
    ruleText:
      "If oral cholera vaccine (Vaxchora) is selected: doxycycline is a systemic antibiotic. Do not give Vaxchora if doxycycline was taken within the previous 14 days. Avoid doxycycline for at least 10 days after Vaxchora administration. This restriction does not apply to Dukoral.",
    schedulingNote:
      "If using Vaxchora: give Vaxchora first, then wait at least 10 days before starting doxycycline. Doxycycline for malaria prophylaxis starts one to two days before departure ({departureDate}). Ensure the 10-day gap is achievable.",
  },

  // --- RULE 6: Hepatitis B alongside other vaccines (HEPLISAV B conditional) ---
  {
    ruleId: "hep-b-heplisav-any-vaccine",
    pgdIdA: PGD_IDS.HEP_B,
    pgdIdB: null,
    type: "product-conditional",
    ruleText:
      "Hepatitis B is in the basket alongside other vaccines. If HEPLISAV B is the intended product, it must NOT be co-administered with any other vaccine at the same visit. If same-visit administration with other vaccines is planned, use Engerix B or HBvaxPRO instead, or schedule HEPLISAV B on a separate date.",
    schedulingNote:
      "Confirm which hepatitis B product will be used before scheduling the visit. HEPLISAV B requires a dedicated visit with no other vaccines.",
  },

  // --- RULE 7: Chikungunya alongside any other vaccine (VIMKUNYA advisory) ---
  {
    ruleId: "chikungunya-any-vaccine",
    pgdIdA: PGD_IDS.CHIKUNGUNYA,
    pgdIdB: null,
    type: "advisory",
    ruleText:
      "Co-administration of VIMKUNYA (chikungunya) with other vaccines has not been studied. Space the chikungunya vaccine from other vaccines where clinically feasible.",
    schedulingNote:
      "Consider giving VIMKUNYA at a separate visit from other vaccines where the schedule allows.",
  },

  // --- RULE 8: MenACWY + DTP Revaxis (Nimenrix / MenQuadfi conditional) ---
  {
    ruleId: "menacwy-dtp-revaxis",
    pgdIdA: PGD_IDS.MENACWY,
    pgdIdB: PGD_IDS.DTP_REVAXIS,
    type: "product-conditional",
    ruleText:
      "If Nimenrix or MenQuadfi is the selected MenACWY product: co-administer with Revaxis at the same visit, or give MenACWY at least one month before Revaxis. This restriction does not apply to Menveo.",
    schedulingNote:
      "If using Nimenrix or MenQuadfi: either give both at the same visit, or ensure MenACWY is given at least one month before Revaxis.",
  },

  // --- RULE 9: Norethisterone + doxycycline ---
  {
    ruleId: "norethisterone-doxycycline",
    pgdIdA: PGD_IDS.NORETHISTERONE,
    pgdIdB: PGD_IDS.DOXYCYCLINE,
    type: "advisory",
    ruleText:
      "Doxycycline may reduce norethisterone efficacy and increase the risk of breakthrough bleeding. Advise barrier contraception throughout the doxycycline course and for seven days after stopping.",
    schedulingNote:
      "Counsel the patient on reduced contraceptive reliability and the need for barrier contraception during and for seven days after the doxycycline course.",
  },
];
